import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, fmtINR, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toCSV, downloadCSV, inDateRange } from "@/lib/csv";
import { PageHeader, Card, Btn, Field, Input, Select, Combobox, Empty } from "@/components/UI";
import { toast } from "sonner";

const emptyLine = { product_id: "", boxes: "", rate: "" };
const emptyForm = () => ({
  date: today(),
  customer_id: "",
  lines: [{ ...emptyLine }],
  dc_number: "",
  transporter_name: "",
  vehicle_no: "",
  eway_bill_no: "",
  consignee_name: "",
  consignee_address: "",
  consignee_gst_no: "",
  notes: "",
});

export default function DispatchPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stock, setStock] = useState([]);
  const [f, setF] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    api.get("/dispatch").then((r) => setList(r.data));
    api.get("/stock").then((r) => setStock(r.data));
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/parties").then((r) => setCustomers(r.data.filter((p) => p.type === "customer")));
    api.get("/settings/company").then((r) => (window._slkc_co = r.data)).catch(() => {});
  }, []);

  const stockRowFor = (pid) => stock.find((s) => s.id === pid) || {};
  const stockPcsFor = (pid) => stockRowFor(pid).stock_pcs || 0;
  const stockBoxesFor = (pid) => stockRowFor(pid).stock_boxes || 0;
  const productById = (pid) => products.find((p) => p.id === pid);
  const ppbFor = (pid) => productById(pid)?.pieces_per_box || 0;

  const updateLine = (idx, patch) => {
    setF((prev) => {
      const next = [...prev.lines];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, lines: next };
    });
  };
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setF((prev) => ({
    ...prev,
    lines: prev.lines.length === 1 ? [{ ...emptyLine }] : prev.lines.filter((_, i) => i !== idx),
  }));

  const startEdit = (d) => {
    setEditing(d.id);
    const lines = (d.lines && d.lines.length > 0)
      ? d.lines.map((l) => ({ product_id: l.product_id, boxes: String(l.boxes || 0), rate: String(l.rate || 0) }))
      : [{ product_id: d.product_id || "", boxes: String(d.boxes || 0), rate: String(d.rate || 0) }];
    setF({
      date: d.date, customer_id: d.customer_id, lines,
      dc_number: d.dc_number || "", transporter_name: d.transporter_name || "",
      vehicle_no: d.vehicle_no || "", eway_bill_no: d.eway_bill_no || "",
      consignee_name: d.consignee_name || "", consignee_address: d.consignee_address || "",
      consignee_gst_no: d.consignee_gst_no || "", notes: d.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(emptyForm()); };

  const totals = useMemo(() => {
    let pcs = 0, boxes = 0, amt = 0;
    f.lines.forEach((l) => {
      const b = Number(l.boxes) || 0;
      const ppb = ppbFor(l.product_id);
      const p = b * ppb;
      pcs += p;
      boxes += b;
      amt += p * (Number(l.rate) || 0);
    });
    return { pcs, boxes, amt: Math.round(amt * 100) / 100 };
    // eslint-disable-next-line
  }, [f.lines, products]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = f.lines.filter((l) => l.product_id && Number(l.boxes) > 0);
    if (clean.length === 0) {
      toast.error("Add at least one product line with boxes > 0");
      return;
    }
    for (const l of clean) {
      if (ppbFor(l.product_id) <= 0) {
        toast.error("Product needs 'pieces per box' set before dispatching in boxes");
        return;
      }
    }
    const payload = {
      date: f.date, customer_id: f.customer_id,
      lines: clean.map((l) => ({
        product_id: l.product_id,
        boxes: parseInt(l.boxes),
        rate: Number(l.rate || 0),
      })),
      dc_number: f.dc_number, transporter_name: f.transporter_name,
      vehicle_no: f.vehicle_no, eway_bill_no: f.eway_bill_no,
      consignee_name: f.consignee_name, consignee_address: f.consignee_address,
      consignee_gst_no: f.consignee_gst_no, notes: f.notes,
    };
    try {
      if (editing) {
        await api.put(`/dispatch/${editing}`, payload);
        toast.success("Dispatch updated · stock re-adjusted");
      } else {
        await api.post("/dispatch", payload);
        toast.success("Dispatched · stock adjusted");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const printDispatch = (d) => {
    const cn = d.customer_name || "—";
    const co = window._slkc_co || {};
    const lines = (d.lines && d.lines.length > 0)
      ? d.lines
      : [{ product_label: d.product_label, pcs: d.pcs, boxes: d.boxes, rate: d.rate, amount: d.amount }];
    const rowsHtml = lines.map((l, i) => `<tr><td class="c">${i + 1}</td><td>${(l.product_label || "—").replace(/</g, "&lt;")}</td><td class="r">${l.pcs || 0}</td><td class="r">${l.boxes || 0}</td></tr>`).join("");
    const totalPcs = d.total_pcs ?? d.pcs ?? 0;
    const totalBoxes = d.total_boxes ?? d.boxes ?? 0;
    const totalAmt = d.total_amount ?? d.amount ?? 0;
    const totalRow = `<tr style="font-weight:900;background:#f5f5f7"><td colspan="2" class="r">Totals</td><td class="r">${totalPcs}</td><td class="r">${totalBoxes}</td></tr>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Dispatch ${d.dc_number || d.id.slice(0,8)}</title><style>@page{size:A4;margin:14mm}*{box-sizing:border-box}body{font:12px "Helvetica Neue",Arial;color:#111;margin:0}.head{display:flex;justify-content:space-between;border-bottom:2px solid #111;padding-bottom:10px}.brand{font-weight:900;font-size:22px;letter-spacing:.05em}.brand small{display:block;font-size:10px;color:#555;margin-top:4px;letter-spacing:.2em}.brand .sub{display:block;font-size:10px;margin-top:3px}.doc{text-align:right}.doc .title{font-size:14px;font-weight:800;letter-spacing:.2em;text-transform:uppercase}.doc .num{font-size:22px;font-weight:900}.doc .stamp{display:inline-block;margin-top:8px;padding:4px 10px;border:1.5px solid #0369a1;color:#0369a1;font-weight:800;letter-spacing:.18em;font-size:10px;text-transform:uppercase}.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px}.parties .box{border:1px solid #ddd;padding:10px 12px}.parties .lab{font-size:8.5px;color:#666;text-transform:uppercase;letter-spacing:.2em;margin-bottom:3px}.parties .val{font-weight:800;font-size:13px}.parties .sub{font-size:10.5px;margin-top:3px;line-height:1.35}.transport{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:12px;padding:8px 0;border-top:1px dashed #ccc;border-bottom:1px solid #ddd}.transport .lab{font-size:8.5px;color:#666;text-transform:uppercase;letter-spacing:.2em}.transport .val{font-weight:700;font-size:12px;margin-top:2px}table{width:100%;border-collapse:collapse;margin-top:14px}th{text-align:left;text-transform:uppercase;letter-spacing:.15em;font-size:9px;padding:8px 6px;border-bottom:2px solid #111}td{padding:10px 6px;border-bottom:1px dashed #ccc}.r{text-align:right;font-variant-numeric:tabular-nums}.c{text-align:center;width:28px}.sign{margin-top:60px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:30px}.sign .box{border-top:1px solid #111;padding-top:6px;font-size:10px;color:#555;text-transform:uppercase;letter-spacing:.2em}.foot{margin-top:30px;font-size:9px;color:#888;text-align:center;letter-spacing:.2em;text-transform:uppercase}</style></head><body><div class="head"><div class="brand">${co.company_name || "SLKC"}<small>${co.tagline || "Selvalakshmi Knits & Co."}</small>${co.address ? `<span class="sub">${String(co.address).replace(/</g, "&lt;")}</span>` : ""}${co.gst_no ? `<span class="sub"><b>GSTIN:</b> ${co.gst_no}</span>` : ""}</div><div class="doc"><div class="title">Delivery Challan · Dispatch</div><div class="num">${d.dc_number || d.id.slice(0,8).toUpperCase()}</div></div></div><div class="parties"><div class="box"><div class="lab">Consignor (From)</div><div class="val">${co.company_name || "SLKC"}</div>${co.address ? `<div class="sub">${String(co.address).replace(/</g, "&lt;")}</div>` : ""}${co.gst_no ? `<div class="sub"><b>GSTIN:</b> ${co.gst_no}</div>` : ""}</div><div class="box"><div class="lab">Consignee (To)</div><div class="val">${d.consignee_name || cn}</div>${d.consignee_address ? `<div class="sub">${String(d.consignee_address).replace(/</g, "&lt;")}</div>` : ""}${d.consignee_gst_no ? `<div class="sub"><b>GSTIN:</b> ${d.consignee_gst_no}</div>` : ""}${!d.consignee_name ? `<div class="sub" style="color:#888">Billing: ${cn}</div>` : ""}</div></div><div class="transport"><div><div class="lab">Date</div><div class="val">${d.date}</div></div><div><div class="lab">Transporter</div><div class="val">${d.transporter_name || "—"}</div></div><div><div class="lab">Vehicle #</div><div class="val">${d.vehicle_no || "—"}</div></div><div><div class="lab">E-way Bill</div><div class="val">${d.eway_bill_no || "—"}</div></div></div><table><thead><tr><th>#</th><th>Item</th><th class="r">Pcs</th><th class="r">Boxes</th></tr></thead><tbody>${rowsHtml}${totalRow}</tbody></table>${d.notes ? `<div style="margin-top:14px;font-size:11px"><b>Notes:</b> ${String(d.notes).replace(/</g, "&lt;")}</div>` : ""}<div class="sign"><div class="box">Prepared by</div><div class="box">Driver Signature</div><div class="box">Consignee Signature</div></div><div class="foot">Computer-generated · SLKC Manufacturing OS</div><script>window.onload=()=>{window.print()}</script></body></html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return toast.error("Pop-up blocked. Allow pop-ups to print.");
    w.document.write(html); w.document.close();
  };

  const del = async (id) => {
    if (!window.confirm("Delete? Dispatched stock will return to inventory.")) return;
    await api.delete(`/dispatch/${id}`);
    load();
  };

  const filtered = useMemo(() => list.filter((d) => inDateRange(d.date, from, to)), [list, from, to]);

  const exportCSV = () => {
    // Flatten multi-line dispatches for CSV
    const rows = [];
    filtered.forEach((d) => {
      const lines = (d.lines && d.lines.length > 0) ? d.lines : [{ product_label: d.product_label, pcs: d.pcs, boxes: d.boxes, rate: d.rate, amount: d.amount }];
      lines.forEach((l) => rows.push({
        date: d.date, customer_name: d.customer_name, product_label: l.product_label,
        pcs: l.pcs, boxes: l.boxes, rate: l.rate, amount: l.amount,
        created_by_name: d.created_by_name, notes: d.notes,
      }));
    });
    const columns = [
      { key: "date", label: "Date" }, { key: "customer_name", label: "Customer" },
      { key: "product_label", label: "Product" }, { key: "pcs", label: "Pcs" }, { key: "boxes", label: "Boxes" },
      { key: "rate", label: "Rate" }, { key: "amount", label: "Amount" },
      { key: "created_by_name", label: "By" }, { key: "notes", label: "Notes" },
    ];
    downloadCSV(`slkc-dispatch-${from || "all"}-${to || "today"}.csv`, toCSV(rows, columns));
  };

  return (
    <div>
      <PageHeader
        label="Stage 09"
        title="Dispatch"
        subtitle="Multi-line · Box-driven · Auto stock adjustment"
        right={
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="dispatch-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="dispatch-to" />
            <button onClick={exportCSV} data-testid="dispatch-export-csv" className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background hover:-translate-y-[1px] transition-transform">↓ CSV</button>
          </div>
        }
      />
      <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card title={editing ? "Edit Dispatch" : "New Dispatch"} className="xl:col-span-1" right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="dispatch-form">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
              <Field label="DC #"><Input placeholder="Auto if blank" value={f.dc_number} onChange={(e) => setF({ ...f, dc_number: e.target.value })} data-testid="dispatch-dc-number" /></Field>
            </div>
            <Field label="Customer">
              <Combobox
                testid="dispatch-customer"
                required
                value={f.customer_id}
                onChange={(v) => setF({ ...f, customer_id: v })}
                placeholder="Type to search customer…"
                options={customers.map((p) => ({ value: p.id, label: p.name, hint: p.phone || p.gst_no || "" }))}
              />
            </Field>

            <div className="border border-border p-3 bg-white space-y-2" data-testid="dispatch-lines">
              <div className="flex items-center justify-between">
                <div className="label-caps">Products · Box-driven</div>
                <button type="button" onClick={addLine} data-testid="dispatch-add-line" className="label-caps text-accent hover:underline">+ Add Product</button>
              </div>
              {f.lines.map((l, idx) => {
                const ppb = ppbFor(l.product_id);
                const stkPcs = stockPcsFor(l.product_id);
                const stkBoxes = stockBoxesFor(l.product_id);
                const boxes = Number(l.boxes) || 0;
                const pcs = boxes * ppb;
                const amt = pcs * (Number(l.rate) || 0);
                return (
                  <div key={idx} className="border border-dashed border-border p-2 bg-secondary/40 space-y-2" data-testid={`dispatch-line-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Line {idx + 1}</span>
                      {f.lines.length > 1 && (
                        <button type="button" onClick={() => removeLine(idx)} className="label-caps hover:text-destructive" data-testid={`dispatch-remove-line-${idx}`}>Remove</button>
                      )}
                    </div>
                    <Combobox
                      testid={`dispatch-product-${idx}`}
                      required
                      value={l.product_id}
                      onChange={(v) => updateLine(idx, { product_id: v })}
                      placeholder="Type to search product / SKU / size…"
                      options={products.map((p) => {
                        const st = stock.find((s) => s.id === p.id) || {};
                        const sb = st.stock_boxes || 0;
                        const sp = st.stock_pcs || 0;
                        return {
                          value: p.id,
                          label: `${p.style_name} / ${p.sku} / ${p.size} / ${p.color}`,
                          hint: `Stock: ${sb} box${sb === 1 ? "" : "es"} (${sp} pcs)`,
                        };
                      })}
                    />
                    {l.product_id && (
                      <div className="text-[11px] font-mono tabular flex gap-3 flex-wrap">
                        <span>Available: <b>{stkBoxes} box{stkBoxes === 1 ? "" : "es"}</b> · {stkPcs} pcs</span>
                        <span>{ppb > 0 ? `${ppb} pcs / box` : <span className="text-destructive">No pcs/box set on product</span>}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Boxes">
                        <Input
                          type="number" min="1" required
                          value={l.boxes}
                          onChange={(e) => updateLine(idx, { boxes: e.target.value })}
                          disabled={!l.product_id || ppb === 0}
                          data-testid={`dispatch-boxes-${idx}`}
                        />
                      </Field>
                      <Field label="Pcs (auto)">
                        <Input value={pcs || ""} readOnly disabled data-testid={`dispatch-pcs-${idx}`} />
                      </Field>
                      {isAdmin && (
                        <Field label="Rate ₹">
                          <Input type="number" step="0.01" value={l.rate} onChange={(e) => updateLine(idx, { rate: e.target.value })} data-testid={`dispatch-rate-${idx}`} />
                        </Field>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="text-[11px] tabular text-right">Amount: <b>{fmtINR(amt)}</b></div>
                    )}
                  </div>
                );
              })}
              <div className="border-t border-border pt-2 flex items-center justify-between text-sm font-mono tabular" data-testid="dispatch-totals">
                <span>Totals</span>
                <span><b>{totals.boxes}</b> box{totals.boxes === 1 ? "" : "es"} · <b>{totals.pcs}</b> pcs{isAdmin ? ` · ${fmtINR(totals.amt)}` : ""}</span>
              </div>
            </div>

            <div className="border border-border p-3 bg-secondary space-y-2">
              <div className="label-caps">Transporter + Consignee</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Vehicle #"><Input placeholder="TN 12 AB 3456" value={f.vehicle_no} onChange={(e) => setF({ ...f, vehicle_no: e.target.value.toUpperCase() })} data-testid="dispatch-vehicle" /></Field>
                <Field label="Transporter Name"><Input value={f.transporter_name} onChange={(e) => setF({ ...f, transporter_name: e.target.value })} data-testid="dispatch-transporter" /></Field>
                <Field label="E-way Bill #"><Input value={f.eway_bill_no} onChange={(e) => setF({ ...f, eway_bill_no: e.target.value })} data-testid="dispatch-eway" /></Field>
                <Field label="Consignee Name"><Input value={f.consignee_name} onChange={(e) => setF({ ...f, consignee_name: e.target.value })} data-testid="dispatch-consignee-name" /></Field>
              </div>
              <Field label="Consignee Address">
                <textarea rows={2} value={f.consignee_address} onChange={(e) => setF({ ...f, consignee_address: e.target.value })} data-testid="dispatch-consignee-addr" className="w-full px-3 py-2 bg-white border border-border font-mono text-xs resize-none" />
              </Field>
              <Field label="Consignee GSTIN"><Input value={f.consignee_gst_no} onChange={(e) => setF({ ...f, consignee_gst_no: e.target.value.toUpperCase() })} data-testid="dispatch-consignee-gst" /></Field>
            </div>

            <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>

            <Btn data-testid="dispatch-submit">{editing ? "Save Changes" : "Dispatch"}</Btn>
          </form>
        </Card>

        <Card title={`Dispatch History (${filtered.length})`} className="xl:col-span-2">
          <div className="table-scroll hidden md:block">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Customer</th><th>Products</th><th className="text-right">Boxes</th><th className="text-right">Pcs</th>{isAdmin && <th className="text-right">Amount</th>}<th>By</th><th></th></tr></thead>
              <tbody>
                {filtered.map((d) => {
                  const lines = (d.lines && d.lines.length > 0) ? d.lines : [{ product_label: d.product_label, pcs: d.pcs, boxes: d.boxes }];
                  const totalPcs = d.total_pcs ?? d.pcs ?? 0;
                  const totalBoxes = d.total_boxes ?? d.boxes ?? 0;
                  const totalAmt = d.total_amount ?? d.amount ?? 0;
                  return (
                    <tr key={d.id} className={editing === d.id ? "bg-secondary align-top" : "align-top"}>
                      <td className="font-mono">{d.date}</td>
                      <td>{d.customer_name}</td>
                      <td>
                        <ul className="space-y-0.5">
                          {lines.map((l, i) => (
                            <li key={i} className="text-xs">
                              <span className="font-mono">{l.boxes || 0}bx</span> <span className="text-muted-foreground">·</span> {l.product_label || "—"}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="tabular text-right font-bold">{totalBoxes}</td>
                      <td className="tabular text-right">{totalPcs}</td>
                      {isAdmin && <td className="tabular text-right">{fmtINR(totalAmt)}</td>}
                      <td className="text-xs text-muted-foreground">{d.created_by_name || "—"}</td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => printDispatch(d)} className="label-caps hover:text-foreground mr-3" data-testid={`dispatch-print-${d.id}`}>Print</button>
                        {isAdmin && (
                          <>
                            <button onClick={() => startEdit(d)} className="label-caps hover:text-foreground mr-3" data-testid={`dispatch-edit-${d.id}`}>Edit</button>
                            <button onClick={() => del(d.id)} className="label-caps hover:text-destructive">Del</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7}><Empty text="No dispatches in this range" /></td></tr>}
              </tbody>
            </table>
          </div>
          {/* Mobile: card-per-dispatch — no horizontal scroll. */}
          <div className="md:hidden space-y-3">
            {filtered.map((d) => {
              const lines = (d.lines && d.lines.length > 0) ? d.lines : [{ product_label: d.product_label, pcs: d.pcs, boxes: d.boxes }];
              const totalPcs = d.total_pcs ?? d.pcs ?? 0;
              const totalBoxes = d.total_boxes ?? d.boxes ?? 0;
              const totalAmt = d.total_amount ?? d.amount ?? 0;
              return (
                <div key={d.id} className="border border-border bg-white p-3" data-testid={`dispatch-card-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{d.customer_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{d.date} · by {d.created_by_name || "—"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="tabular text-lg font-bold">{totalBoxes}<span className="text-xs text-muted-foreground">bx</span></div>
                      <div className="tabular text-xs text-muted-foreground">{totalPcs} pcs</div>
                    </div>
                  </div>
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {lines.map((l, i) => (
                      <li key={i}><span className="font-mono">{l.boxes || 0}bx</span> <span className="text-muted-foreground">·</span> {l.product_label || "—"}</li>
                    ))}
                  </ul>
                  {isAdmin && (
                    <div className="mt-2 tabular text-xs font-mono">Amount: <b>{fmtINR(totalAmt)}</b></div>
                  )}
                  <div className="mt-3 pt-3 border-t border-border flex gap-2">
                    <button onClick={() => printDispatch(d)} className="flex-1 h-10 border border-border font-mono text-[10px] tracking-[0.15em] uppercase font-bold" data-testid={`dispatch-print-mobile-${d.id}`}>Print</button>
                    {isAdmin && (
                      <>
                        <button onClick={() => startEdit(d)} className="flex-1 h-10 border border-border font-mono text-[10px] tracking-[0.15em] uppercase font-bold" data-testid={`dispatch-edit-mobile-${d.id}`}>Edit</button>
                        <button onClick={() => del(d.id)} className="h-10 px-4 border border-destructive text-destructive font-mono text-[10px] tracking-[0.15em] uppercase font-bold">Del</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <Empty text="No dispatches in this range" />}
          </div>
        </Card>
      </div>
    </div>
  );
}
